import { CdkTrapFocus } from '@angular/cdk/a11y';
import { AsyncPipe, NgIf } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  MatFormField,
  MatInput,
  MatInputModule,
  MatLabel,
} from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import {
  MatTimepicker,
  MatTimepickerInput,
  MatTimepickerToggle,
} from '@angular/material/timepicker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UsersTableComponent } from '../../components/users-table/users-table.component';
import { AuthStateService } from '../../core/services/auth-state.service';
import { Config } from '../../shared/models/config/config.model';
import { ConfigSerialize } from '../../shared/models/config/config.serialize';
import { ConfigModelForm } from '../../shared/models/config/form/config-model.form';
import { ConfigModelMapper } from '../../shared/models/config/form/config-model.mapper';
import { User, UserDetails } from '../../shared/models/user/user.model';
import { DialogService } from '../../shared/services/dialog.service';
import { UserService } from '../../shared/services/user.service';

@Component({
  selector: 'admin-page',
  standalone: true,
  imports: [
    MatTabGroup,
    MatTab,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatLabel,
    MatFormField,
    MatButton,
    MatPaginator,
    AsyncPipe,
    NgIf,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTimepickerInput,
    MatTimepickerToggle,
    MatTimepicker,
    UsersTableComponent,
    MatIconButton,
    CdkTrapFocus,
  ],
  templateUrl: './admin-page.component.html',
})
export class AdminPageComponent implements OnInit {
  protected authStateService = inject(AuthStateService);
  private userService = inject(UserService);
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);

  protected currentUser: User | null | undefined;
  protected users$ = new BehaviorSubject<User[]>([]);
  protected configForm!: FormGroup;

  public readonly isDataImportVisible = signal(false);
  public readonly isDataImportCompleted = signal(false);
  public readonly isDataImportValid = signal(false);

  constructor(private httpClient: HttpClient) {}

  ngOnInit(): void {
    this.currentUser = this.authStateService.user();
    this.refreshUsers();

    this.configForm = this.buildConfigForm({});
    this.initializeConfigForm();
  }

  private buildConfigForm(config: Partial<Config>): FormGroup {
    const configModelMapper = new ConfigModelMapper(ConfigModelForm);
    return configModelMapper.buildForm(config) as FormGroup;
  }

  private initializeConfigForm(): void {
    this.configForm.controls['workingYear'].setValue(
      this.currentUser?.workingYear,
    );
    this.configForm.controls['startDate'].setValue(
      new Date(this.currentUser?.startDate!),
    );
    this.configForm.controls['startTime'].setValue(
      new Date(this.currentUser?.startDate!),
    );
    this.configForm.controls['endDate'].setValue(
      new Date(this.currentUser?.endDate!),
    );
    this.configForm.controls['endTime'].setValue(
      new Date(this.currentUser?.endDate!),
    );
  }

  private refreshUsers(): void {
    this.userService
      .getUsers()
      .pipe(tap((result) => this.users$.next(result)))
      .subscribe();
  }

  public onSubmit(): void {
    if (this.configForm?.valid) {
      const configModelMapper = new ConfigModelMapper(ConfigModelForm);
      const config = configModelMapper.toObject(this.configForm.value);

      this.updateConfig(config);
    }
  }

  public updateConfig(configValue: any): void {
    this.httpClient
      .put<any>(
        `${environment.apiUrl}/admin/configs`,
        ConfigSerialize.toConfig(configValue),
      )
      .pipe(
        tap((response) => {
          this.dialogService.showSuccess(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.UPDATE_CONFIG_SUCCESSFUL',
            ),
          );
        }),
        catchError((error) => {
          this.dialogService.showError(
            this.translateService.instant(
              'ADMIN_PAGE.DIALOG_SERVICE.UPDATE_CONFIG_ERROR',
            ),
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  public handleNew(userDetails: UserDetails) {
    this.userService.new(userDetails);
    this.refreshUsers();
  }

  public handleImport(users: any) {
    const validation = this.userService.validate(users);
    if (!validation.isValid) {
      const errorMessage = validation.errors.join('\n');
      this.dialogService.showError(errorMessage);
      this.isDataImportValid.set(false);
      this.isDataImportCompleted.set(true);
      return;
    } else {
      this.isDataImportValid.set(true);
      this.userService.import(users);
      this.refreshUsers();
      setTimeout(() => {
        this.isDataImportCompleted.set(true);
      }, 1000);
    }
  }

  public handleEdit(user: User) {
    this.userService.update(user);
    this.refreshUsers();
  }

  public handleDelete(user: User) {
    this.userService.delete(user.id);
    this.refreshUsers();
  }

  public handleDeleteAllUsers() {
    this.userService.deleteAll();
    this.refreshUsers();
  }
}
